import { z } from 'zod';
import { RiskReactionExprSchema as RiskReactionExprDomainSchema, RiskReactionStepsConfigSchema, } from '@wit-support/risk-alerts';
export const RiskSourceSchema = z.enum(['chargeblast']);
const _pinSource = true;
export const RiskAlertKindSchema = z.enum(['fraud', 'dispute', 'rdr', 'cancel', 'info', 'unknown']);
const _pinKind = true;
export const RiskNetworkSchema = z.enum([
    'Ethoca',
    'CDRN',
    'RDR',
    'Discover',
    'AMEX',
    'JCB',
    'Cancel',
    'TC40',
    'SAFE',
    'PayPal',
]);
const _pinNetwork = true;
export const RiskCaseStatusSchema = z.enum(['open', 'actioned', 'resolved', 'dismissed']);
const _pinCaseStatus = true;
export const RiskIdentificationStatusSchema = z.enum([
    'pending',
    'resolved',
    'ambiguous',
    'not_found',
    'unavailable',
]);
const _pinIdent = true;
export const RiskIdentifiedBySchema = z.enum(['matcher', 'manual']);
const _pinIdentBy = true;
export const RiskCorrelationBasisSchema = z.enum([
    'external_order',
    'arn',
    'network_id',
    'fuzzy',
    'none',
]);
const _pinBasis = true;
export const RiskDuplicateClassSchema = z.enum(['intra_source', 'cross_source', 'none']);
const _pinDup = true;
/**
 * Путь, которым кейс привязался к клиенту без человека. Первые ЧЕТЫРЕ
 * значения — откалиброванные пути ПЛАТЁЖНОГО СЕРВИСА (`autoLinkBasis` его
 * ответа; `code+amount` добавлен редакцией контракта от 2026-08-27),
 * `single-customer` — НАШ: апстрим не смог выбрать конкретное списание, но все
 * кандидаты оказались одним клиентом, а кейс привязывает человека, а не
 * списание. Смысл каждого — в `RiskAutoLinkBasis` (`@wit-support/risk-alerts`).
 */
export const RiskAutoLinkBasisSchema = z.enum([
    'network-id',
    'code+card',
    'code+token',
    'code+amount',
    'single-customer',
]);
const _pinAutoLink = true;
/** Уверенность апстрима в списании. Считает её он; см. `RiskMatchConfidence`. */
export const RiskMatchConfidenceSchema = z.enum(['confirmed', 'high', 'medium', 'low', 'none']);
const _pinConfidence = true;
/**
 * Совпал ли код авторизации как есть или с восстановленным ведущим нулём.
 * Различие показывается оператору: «коды выглядят по-разному, а совпадение
 * засчитано» без него читается как ошибка.
 */
export const RiskAuthCodeMatchSchema = z.enum(['exact', 'zero-padded']);
const _pinAuthCodeMatch = true;
/**
 * ПОЧЕМУ суммы сходятся или не сходятся — ответ на самый частый вопрос
 * оператора о кандидате («алерт говорит 139.38, списание 143.42, это те же
 * деньги?»). Значения описаны в `RiskAmountMatch`
 * (`@wit-support/risk-alerts`).
 *
 * `kind` обязателен: объект без вердикта не сообщает ничего, и рисовать
 * «сравнение сумм» пустым хуже, чем не рисовать его вовсе — незнакомый вердикт
 * гасит объект целиком (`.catch(null)` на поле-владельце ниже).
 */
export const RiskAmountMatchDTO = z.object({
    kind: z.enum(['exact', 'fee-markup', 'fx-plausible', 'unknown-currency', 'mismatch']),
    /**
     * Подразумеваемый курс за вердиктом `fx-plausible`.
     *
     * `.catch(null)` — потому что это УТОЧНЕНИЕ, а не вердикт. Без него негодный
     * тип в курсе гасил бы весь объект вместе с `kind`, то есть оператор терял
     * бы ответ «суммы сходятся» из-за поля, которое лишь объясняет, насколько.
     * Гасить объект целиком правильно ровно на незнакомом `kind` — там гасить
     * нечего и рисовать нечего.
     */
    impliedRate: z.number().nullable().default(null).catch(null),
    /** На сколько процентов списание превышает алерт при `fee-markup`. Тоже уточнение. */
    markupPct: z.number().nullable().default(null).catch(null),
});
const _pinAmountMatchKind = true;
// Сцепки существуют только ради проверки типов — гасим no-unused-vars одним
// обращением, чтобы не рассыпать eslint-disable по файлу.
void [
    _pinSource,
    _pinKind,
    _pinNetwork,
    _pinCaseStatus,
    _pinIdent,
    _pinIdentBy,
    _pinBasis,
    _pinDup,
    _pinAutoLink,
    _pinConfidence,
    _pinAuthCodeMatch,
    _pinAmountMatchKind,
];
/**
 * Целые центы. Общий кирпич для всех сумм раздела.
 *
 * БЕЗ `nonnegative()`, и это осознанно. Реверсал и кредит приходят от
 * источника с ОТРИЦАТЕЛЬНОЙ суммой — это не «знак попал из поля возврата», а
 * нормальное значение поля. С `nonnegative()` одна такая строка роняет
 * `parse` ВСЕЙ страницы списка, и раздел становится недоступен целиком; знак
 * при этом несёт смысл («вернули», а не «списали») и обязан доехать до
 * оператора как есть. Дробь по-прежнему запрещена: это признак того, что
 * конвертацию на границе адаптера обошли.
 */
const AmountCents = z.number().int();
/**
 * Полный словарь reason-кодов Chargeblast (`AlertReasonCode`). Живёт здесь, а
 * не в модуле admin-web: выпадающий список оператора и подсказка в ответе
 * роута действия обязаны показывать ОДИН набор — расхождение означает, что
 * оператор выбрал код, который источник отвергнет уже после необратимой
 * отправки.
 *
 * Значение НЕ валидируется схемой запроса (см. `RiskAlertActionRequest`): набор
 * принадлежит ИСТОЧНИКУ и меняется на его стороне, а протухшая копия у нас
 * заблокировала бы оператору легальный код. Это справочник для UI, а не
 * ограничение контракта.
 */
export const RISK_ACTION_REASON_CODES = [
    'Resolved',
    'ResolvedPartial',
    'ResolvedCancelled',
    'AccountCancelled',
    'CancellationProcessed',
    'UnmatchedGeneral',
    'UnmatchedCannotFindTransaction',
    'Duplicate',
    'MIDLost',
    'AlreadyRefunded',
    'AlreadyChargeback',
    'Ineligible',
    'TDS',
    'CannotCancel',
    'MatchedWillShip',
    'EscalateChargeback',
    'ContactedShipper',
    'WIP',
    'Error',
    'None',
    'NotMyDescriptor',
    'Unactioned',
];
/**
 * Одно списание кандидата — то, по чему оператор и различает кандидатов между
 * собой: дата, сумма, признак диспута, страна и тип карты.
 *
 * Суммы в МИНИМАЛЬНЫХ единицах, как везде в этом репозитории: `92871` + `PYG`,
 * а не `928.71`. Терпимость к типам здесь намеренная — это снимок чужого
 * ответа, лежащий в jsonb, и негодное значение обязано доехать пустым полем,
 * а не пустой карточкой.
 */
export const RiskCandidateChargeDTO = z.object({
    chargeId: z.string().nullable().default(null),
    /**
     * Stripe PaymentIntent за списанием — редакция поиска от 2026-08-27,
     * `.catch(null)` по правилу окна выката (снимки до этой даты его не несут).
     * Показ-только: возврат и всякое автоматическое решение по-прежнему целятся
     * в `chargeId` — единственный идентификатор, который принимает
     * refund-эндпоинт.
     */
    paymentIntentId: z.string().nullable().default(null).catch(null),
    invoiceId: z.string().nullable().default(null),
    createdAt: z.string().nullable().default(null),
    amountCents: z.number().nullable().default(null),
    currency: z.string().nullable().default(null),
    /** У Stripe по этому списанию уже есть диспут — сильнейший признак выбора. */
    disputed: z.boolean().nullable().default(null),
    refunded: z.boolean().nullable().default(null),
    amountRefundedCents: z.number().nullable().default(null),
    cardBrand: z.string().nullable().default(null),
    cardFunding: z.string().nullable().default(null),
    cardCountry: z.string().nullable().default(null),
    walletType: z.string().nullable().default(null),
    /** Цифры, напечатанные на пластике и видимые в выписке. */
    cardLast4: z.string().nullable().default(null),
    /**
     * Номер УСТРОЙСТВА Apple/Google Pay — регулярно отличается от `cardLast4`,
     * и алерт может нести любой из двух.
     *
     * Отдельным полем, а не свёрнутым в `cardLast4`: свёртка и прятала ответ. На
     * живой паре кандидатов с прода оба показывали `7703`, тогда как номера
     * устройств были `5439` и `4548` — оператор, сверяющий алерт с номером
     * устройства, отверг бы верного кандидата как «не тот».
     *
     * `.catch(null)` — как и у соседей ниже: во время выката новый бандл читает
     * поле, которого старый admin-api ещё не отдаёт.
     */
    walletLast4: z.string().nullable().default(null).catch(null),
    matchedBy: z.array(z.string()).default([]),
    /**
     * `near` — совпал СОСЕДНИЙ день, а не спрошенный. Незнакомое значение
     * становится `null`: выдать соседний день за точное совпадение нельзя, но и
     * ронять из-за него разбор ответа тоже нельзя.
     */
    dateMatch: z.enum(['exact', 'near']).nullable().default(null).catch(null),
    /**
     * Всё, что ниже, приехало с редакцией поиска списаний от 2026-08-25. Приём
     * тот же, что у `dateMatch`: незнакомое значение становится `null`, а не
     * роняет разбор — снимок ответа матчера лежит в jsonb КАК ЕСТЬ, и снимки,
     * записанные старой формой (на 25.08.2026 их 230 — снимок хранится только у
     * кейса со статусом `ambiguous`), обязаны доезжать с пустыми новыми полями,
     * а не пустой карточкой кейса.
     */
    confidence: RiskMatchConfidenceSchema.nullable().default(null).catch(null),
    /**
     * `succeeded` | `pending` | `failed`, как его отдал апстрим.
     *
     * СТРОКА, а не перечисление: набор статусов принадлежит Stripe, и незнакомое
     * значение полезнее показать оператору как есть, чем обнулить, — в отличие
     * от `confidence`/`dateMatch`, где незнакомый уровень нечем нарисовать.
     *
     * Различие решающее: фрод-алерт регулярно ссылается на авторизацию,
     * записанную у нас как `failed` — код авторизации у попытки есть, деньги не
     * двигались. Живой ответ прода по неразобранному кейсу вернул ровно такую
     * пару — `failed`-попытку и `succeeded`-списание на одних и тех же последних
     * четырёх цифрах, причём `failed` шла в выдаче первой. Без этого поля две
     * строки выглядят одинаково, и оператор выбирает между ними вслепую.
     */
    status: z.string().nullable().default(null).catch(null),
    authCodeMatch: RiskAuthCodeMatchSchema.nullable().default(null).catch(null),
    /**
     * Списание прошло по СЕТЕВОМУ ТОКЕНУ: Stripe видел цифры токена, а алерт
     * эмитента несёт цифры настоящей карты. Единственный факт, объясняющий
     * верное совпадение с непохожим `last4`, — без него оператор отвергает
     * правильного кандидата за «неправильный вид».
     */
    networkTokenUsed: z.boolean().nullable().default(null).catch(null),
    /** Идентификатор транзакции у сети — ключ склейки между системами. */
    networkTransactionId: z.string().nullable().default(null).catch(null),
    /**
     * Вердикт сравнения сумм. `.catch(null)` гасит объект ЦЕЛИКОМ, а не по полю:
     * вердикт с незнакомым `kind` не поддаётся показу — «сравнение сумм: ???»
     * оператору не отвечает, а сглаживать его в `mismatch` значило бы заявить
     * то, чего апстрим не говорил.
     */
    amountMatch: RiskAmountMatchDTO.nullable().default(null).catch(null),
});
/**
 * Кандидат в клиенты, отданный матчером при `ambiguous`. Все поля nullable:
 * платёжный сервис возвращает то, что у него есть, и отсутствие имени — не
 * ошибка, а обычное состояние.
 */
export const RiskCustomerCandidateDTO = z.object({
    witUserId: z.string().nullable().default(null),
    email: z.string().nullable().default(null),
    displayName: z.string().nullable().default(null),
    cardLast4: z.string().nullable().default(null),
    matchedBy: z.array(z.string()).default([]),
    stripeCustomerId: z.string().nullable().default(null),
    stripeEmail: z.string().nullable().default(null),
    billingEmail: z.string().nullable().default(null),
    billingName: z.string().nullable().default(null),
    /**
     * Списания кандидата, свежие первыми. У кандидатов, сохранённых до
     * 2026-08-24, их нет вовсе — отсюда `.default([])`: снимок ответа матчера
     * лежит в jsonb как есть, и старая строка обязана доехать пустой, а не
     * уронить карточку кейса.
     */
    charges: z.array(RiskCandidateChargeDTO).default([]),
    /**
     * Насколько апстрим уверен в ЭТОМ человеке — СИЛЬНЕЙШАЯ уверенность среди
     * его списаний: кандидат стоит столько же, сколько его лучшее попадание, и
     * сворачивание трёх месячных списаний подписки к самому слабому из них
     * похоронило бы ровно то списание, о котором алерт.
     *
     * `null` — «снимок старше поля», а НЕ `none`: `none` — это ответ апстрима
     * «ничего не сходится», и путать его с отсутствием ответа нельзя.
     */
    confidence: RiskMatchConfidenceSchema.nullable().default(null).catch(null),
});
/**
 * Отправленное решение по алерту (`risk_alert_action`). Отдаётся ВЛОЖЕННЫМ
 * объектом внутри алерта, а не тремя плоскими полями: три независимо-nullable
 * поля позволяют собрать невозможную комбинацию (статус есть, кода нет) и
 * заставляют каждого читателя проверять три условия там, где смысл один —
 * «решение по алерту уже отправляли или нет».
 *
 * Три статуса, и `in_flight` — не «переходное состояние на полсекунды», а
 * ДОЛГОЖИВУЩИЙ исход: если вызов источника оборвался по таймауту, применился
 * апдейт или нет — неизвестно НИКОМУ, и строка остаётся `in_flight` навсегда,
 * пока человек не сверится с Chargeblast вручную. UI обязан показывать это
 * состояние отдельным текстом, а не как «отправлено» и не как «ошибка».
 */
export const RiskAlertActionDTO = z.object({
    id: z.guid(),
    status: z.enum(['in_flight', 'sent', 'failed']),
    /** Отправленный reason-код (значение `AlertReasonCode` источника). */
    result: z.string(),
    actionAtExpiration: z.boolean().default(false),
    adminId: z.string().nullable().default(null),
    /** Имя оператора; join к `admin`. null, если админа удалили. */
    adminName: z.string().nullable().default(null),
    /** Текст отказа источника; заполнен только у `failed`. */
    error: z.string().nullable().default(null),
    createdAt: z.string().datetime(),
});
/**
 * Один алерт кейса. `sentAction` — ПОСЛЕДНЯЯ строка `risk_alert_action` со
 * статусом `sent` или `in_flight`: именно по ней UI прячет кнопку «Отправить
 * решение», а роут действия отвечает 409. Отдаётся в списке алертов, а не
 * отдельным запросом, потому что карточка кейса без неё не может нарисовать
 * ни одну кнопку.
 */
export const RiskAlertDTO = z.object({
    id: z.guid(),
    source: RiskSourceSchema,
    sourceAlertId: z.string(),
    kind: RiskAlertKindSchema,
    network: RiskNetworkSchema.nullable().default(null),
    actionRequired: z.boolean(),
    correlationBasis: RiskCorrelationBasisSchema,
    duplicateClass: RiskDuplicateClassSchema,
    amountCents: AmountCents.nullable().default(null),
    currency: z.string().nullable().default(null),
    cardLast4: z.string().nullable().default(null),
    cardBrand: z.string().nullable().default(null),
    bin: z.string().nullable().default(null),
    funding: z.string().nullable().default(null),
    issuer: z.string().nullable().default(null),
    country: z.string().nullable().default(null),
    arn: z.string().nullable().default(null),
    authCode: z.string().nullable().default(null),
    networkId: z.string().nullable().default(null),
    descriptor: z.string().nullable().default(null),
    mid: z.string().nullable().default(null),
    processor: z.string().nullable().default(null),
    externalOrder: z.string().nullable().default(null),
    providerCustomerEmail: z.string().nullable().default(null),
    providerCustomerId: z.string().nullable().default(null),
    reasonCode: z.string().nullable().default(null),
    isMatched: z.boolean(),
    isRefunded: z.boolean(),
    acquirerAction: z.string().nullable().default(null),
    transactionAt: z.string().datetime().nullable().default(null),
    alertCreatedAt: z.string().datetime().nullable().default(null),
    providerResolvedAt: z.string().datetime().nullable().default(null),
    firstSeenAt: z.string().datetime().nullable().default(null),
    /** Наше решение по алерту, если его уже отправляли. */
    sentAction: RiskAlertActionDTO.nullable().default(null),
});
/**
 * Ось реакции кейса (`risk_case.reaction_status`). Живёт в `packages/db`
 * (`RiskCaseReactionStatus`) — не пиннуется `AssertEqual`, как соседи выше,
 * потому что `admin-contracts` не зависит от `@wit-support/db` (тот тянет за
 * собой Kysely и Postgres-типы, которых контрактам не нужно) и от него не
 * зависит; четыре значения переписаны здесь по образцу самого стора.
 */
export const RiskCaseReactionStatusSchema = z.enum(['pending', 'done', 'escalated', 'suppressed']);
/**
 * Статус ОДНОЙ строки возврата (`risk_case_refund.status`). Живёт в
 * `packages/db` (`RiskCaseRefundStatus`) — как и `RiskCaseReactionStatusSchema`
 * выше, не пиннуется `AssertEqual`, потому что `admin-contracts` не зависит от
 * `@wit-support/db`; три значения переписаны здесь по образцу того же стора.
 * `in_flight` — не «доля секунды», а полноценный долгоживущий исход: наш
 * процесс мог упасть между вызовом платёжного сервиса и записью ответа, и
 * тогда строка остаётся `in_flight`, пока ручной запуск реакции не реплеит тот
 * же запрос и не дочитает исход (см. `getRefund` в `RiskReactionStore`).
 */
export const RiskCaseRefundStatusSchema = z.enum(['in_flight', 'sent', 'failed']);
/**
 * Сводка возврата по кейсу для карточки (блок «Реакция»). Собирается
 * admin-api из `RiskReactionStore.getRefund` — ЖИВОЙ (`in_flight`/`sent`)
 * строки, либо, если живой нет, новейшей закрытой `failed`. `chargeId` —
 * списание, НА КОТОРОЕ пошёл возврат: оператор обязан видеть цель денежной
 * операции, а не только её статус. Полного ответа платёжного сервиса
 * (`providerStatus` из конверта возврата) здесь намеренно нет — он не хранится
 * отдельной колонкой строки (лежит внутри `response` jsonb вперемешку с
 * остальным телом ответа), и вытаскивать его сюда значило бы заводить второй
 * парсер чужого конверта в DTO ради одного поля; `status='in_flight'` уже
 * говорит оператору всё нужное — «возврат либо ещё выполняется, либо застрял,
 * ручной запуск реакции дочитает исход».
 */
export const RiskCaseRefundSummaryDTO = z.object({
    status: RiskCaseRefundStatusSchema,
    chargeId: z.string(),
});
// ────────────────────────── Прогоны реакций (runs) ──────────────────────────
//
// Объявлены ЗДЕСЬ, а не рядом с правилами дальше по файлу, потому что
// `RiskCaseDetailDTO.lastReactionRun` ниже ссылается на `RiskReactionRunDTO`
// напрямую (без `z.lazy`) — Zod-объекту нужен уже построенный тип поля, а не
// более позднее объявление константы.
export const RiskReactionRunKindSchema = z.enum(['primary', 'duplicate']);
export const RiskReactionRunStatusSchema = z.enum([
    'running',
    'completed',
    'failed',
    'escalated',
    'skipped',
]);
export const RiskReactionStepNameSchema = z.enum([
    'refund',
    'cancel_subscription',
    'respond_alert',
]);
/**
 * `shadow` — прогон в режиме измерения (флаг `risk.reactions_shadow`): шаг
 * записан в журнал, но ни одного реального вызова наружу не было. Отдельное
 * значение статуса шага, а не производное от `run.shadow`: у ОДНОГО прогона
 * могут быть и обычные, и shadow-шаги в переходный период смены флага.
 */
export const RiskReactionStepStatusSchema = z.enum(['ok', 'skipped', 'failed', 'shadow']);
export const RiskReactionStepDTO = z.object({
    id: z.guid(),
    seq: z.number().int(),
    step: RiskReactionStepNameSchema,
    status: RiskReactionStepStatusSchema,
    /** Снимок исхода шага (ответ платёжного сервиса/подавление/причина отказа) — форма своя для каждого `step`. */
    result: z.unknown().nullable().default(null),
    error: z.string().nullable().default(null),
    createdAt: z.string().datetime(),
});
/**
 * Один прогон реакции (`risk_reaction_run`) вместе с его шагами. Отдаётся
 * ВЛОЖЕННЫМ (`case`-карточка держит его под `lastReactionRun`, журнал —
 * плоским списком), шаги — внутри, а не отдельным запросом: карточка кейса
 * без них не может показать, что именно реактор сделал или пропустил.
 */
export const RiskReactionRunDTO = z.object({
    id: z.guid(),
    caseId: z.guid(),
    triggerAlertId: z.guid(),
    kind: RiskReactionRunKindSchema,
    /** `null` — прогон не привязан ни к одному ВКЛЮЧЁННОМУ правилу (например, кейс не совпал ни с одним). */
    ruleId: z.guid().nullable(),
    status: RiskReactionRunStatusSchema,
    shadow: z.boolean(),
    error: z.string().nullable(),
    createdAt: z.string().datetime(),
    finishedAt: z.string().datetime().nullable(),
    steps: z.array(RiskReactionStepDTO).default([]),
});
/**
 * Кейс без вложений — зеркало `RiskCaseRow` из стора. Он же строка списка
 * (`RiskCaseListItemDTO`), он же `case` внутри карточки (`RiskCaseDetailDTO`):
 * списку и карточке нужны ОДНИ И ТЕ ЖЕ поля кейса, и второй набор означал бы
 * второй маппер и разъехавшиеся форматы дат.
 *
 * Счётчик алертов и наборы источников/сетей считаются в запросе (в `risk_case`
 * они НЕ денормализованы — см. спеку), поэтому живут прямо здесь.
 */
export const RiskCaseDTO = z.object({
    id: z.guid(),
    /** Короткий человеческий номер. nullable по правилу окна выката. */
    number: z.number().int().positive().nullable().default(null),
    status: RiskCaseStatusSchema,
    kind: RiskAlertKindSchema,
    actionRequired: z.boolean(),
    amountCents: AmountCents.nullable().default(null),
    currency: z.string().nullable().default(null),
    cardLast4: z.string().nullable().default(null),
    descriptor: z.string().nullable().default(null),
    transactionAt: z.string().datetime().nullable().default(null),
    firstAlertAt: z.string().datetime().nullable().default(null),
    lastAlertAt: z.string().datetime().nullable().default(null),
    witUserId: z.string().nullable().default(null),
    identificationStatus: RiskIdentificationStatusSchema,
    identifiedBy: RiskIdentifiedBySchema.nullable().default(null),
    identifiedAt: z.string().datetime().nullable().default(null),
    /** Кто привязал клиента руками; null, если привязка не ручная. */
    identifiedByAdminId: z.string().nullable().default(null),
    /**
     * Имя оператора, привязавшего клиента руками (join к `admin` в сторе).
     * Показывается рядом с «привязано вручную»: без имени бейдж отвечает «кем-то
     * когда-то», и второй оператор не знает, с кем сверяться перед отвязкой.
     */
    identifiedByAdminName: z.string().nullable().default(null),
    /**
     * Когда матчер спрашивали в последний раз. Нужен и UI («переспросить» на
     * кейсе, который спрашивали минуту назад, — почти всегда ошибка), и хендлеру
     * идентификации как граница «что мы уже знали к прошлой попытке».
     */
    matchAttemptedAt: z.string().datetime().nullable().default(null),
    /**
     * ПОЧЕМУ кейс привязался без человека: каким путём и с какой уверенностью.
     * Оператор, разбирающий неверную привязку, начинает именно отсюда — «совпал
     * идентификатор транзакции сети» и «совпали код авторизации с последними
     * четырьмя цифрами» ведут по разным следам.
     *
     * `null` — путь неизвестен: кейс привязан до 2026-08-25 либо привязан
     * вручную (у человека серверного пути нет). Отдельного `'unknown'` нет —
     * оно стояло бы у большинства строк и не сообщало бы сверх `null` ничего.
     *
     * `.catch(null)` СВЕРХ `.default(null)`, и это два разных случая: `default`
     * закрывает окно выката (новый бандл читает поле, которого старый admin-api
     * ещё не отдаёт), а `catch` — незнакомое ЗНАЧЕНИЕ перечисления, когда
     * платёжный сервис добавит четвёртый откалиброванный путь раньше, чем мы
     * научимся его называть. Новый путь обязан стоить оператору подписи «путь
     * неизвестен», а не всей карточки кейса.
     */
    matchBasis: RiskAutoLinkBasisSchema.nullable().default(null).catch(null),
    /** Уверенность апстрима на момент привязки. `null` ≠ `'none'` — см. выше. */
    matchConfidence: RiskMatchConfidenceSchema.nullable().default(null).catch(null),
    alertCount: z.number().int().nonnegative(),
    sources: z.array(RiskSourceSchema).default([]),
    networks: z.array(RiskNetworkSchema).default([]),
    /**
     * Внутриисточниковый дубликат: за алерт заплачено дважды. Отдельный флаг, а
     * не производная от `alertCount > 1`: два алерта из РАЗНЫХ источников —
     * `cross_source`, платы за второй нет и заявлять нечего.
     */
    hasIntraSourceDuplicate: z.boolean().default(false),
    /**
     * Ось реакции — независимая от «нашего» `status` выше (`open`/`actioned`/…):
     * `status` отвечает «разобран ли кейс человеком», `reactionStatus` — «что
     * сделала (или не сделала) автоматика реакций». `pending` — ещё не
     * тронута, `done`/`escalated` — реактор закончил (успешно или передал
     * человеку), `suppressed` — весь исторический бэклог на момент включения
     * модуля (миграция `20260826185651`) — автоматика его сознательно не
     * трогает. `.default('pending')` по правилу окна выката: новый бандл читает
     * поле, которого старый admin-api ещё не отдаёт.
     */
    reactionStatus: RiskCaseReactionStatusSchema.default('pending'),
    /**
     * Списание, к которому реактор привязал шаги возврата/отмены. `null` —
     * реактор ещё не выбрал списание (кейс не идентифицирован, либо реакция ещё
     * не запускалась). Отдельно от `matchBasis`/`matchConfidence` выше: те
     * объясняют привязку КЛИЕНТА, это — привязку КОНКРЕТНОГО СПИСАНИЯ, на
     * которое пойдёт возврат.
     */
    matchedChargeId: z.string().nullable().default(null),
});
/**
 * Строка списка кейсов. Тождественно равна `RiskCaseDTO` — ровно как в сторе
 * `RiskCaseListRow = RiskCaseRow`. Алиас существует, чтобы имя на проводе
 * читалось по месту употребления; расходиться этим двум формам нельзя.
 */
export const RiskCaseListItemDTO = RiskCaseDTO;
/**
 * Диалог поддержки, вычисленный на чтении (по `wit_user_id` контакта или по
 * email алерта). `conversationId`, а не `id`: строка не описывает сущность
 * «связь», у неё нет собственного идентификатора, и `id` рядом с `id` кейса
 * читается как id связи.
 */
export const RiskCaseConversationDTO = z.object({
    conversationId: z.guid(),
    number: z.number().int().positive().nullable().default(null),
    subject: z.string().nullable().default(null),
    status: z.string(),
    lastActivityAt: z.string().datetime(),
    /**
     * Почему диалог попал в кейс. Оператор обязан различать: связь по
     * `wit_user_id` — это тот же клиент наверняка, связь по email — совпадение
     * адреса, которое может оказаться чужим (общий семейный ящик, опечатка
     * провайдера).
     */
    linkBasis: z.enum(['wit_user_id', 'email']),
});
/**
 * Карточка кейса. Форма ВЛОЖЕННАЯ и единственная: кейс лежит под ключом
 * `case`, вложения — рядом. Зеркало `RiskCaseDetail` из стора, поэтому маппер
 * роута переиспользует маппер строки списка на `detail.case` и не заводит
 * второй, расплющивающий.
 */
export const RiskCaseDetailDTO = z.object({
    case: RiskCaseDTO,
    alerts: z.array(RiskAlertDTO).default([]),
    candidates: z.array(RiskCustomerCandidateDTO).default([]),
    conversations: z.array(RiskCaseConversationDTO).default([]),
    /**
     * ПОСЛЕДНИЙ прогон реакции по кейсу (`risk_reaction_run`, новейший по
     * `created_at, id`), вместе с его шагами. `null` — реакция ни разу не
     * запускалась (обычный случай для только что заведённого кейса) либо стор
     * реакций не подключён к этому процессу admin-api (`ChargebacksRouteDeps.reactions`
     * не задан) — оба случая для карточки неотличимы: блока реакции просто нет.
     * `.default(null)` по правилу окна выката.
     */
    lastReactionRun: RiskReactionRunDTO.nullable().default(null),
    /**
     * Сводка ЖИВОЙ (или, если живой нет, новейшей закрытой) строки возврата по
     * кейсу — `null`, когда возврат ни разу не запускался либо `reactions` не
     * подключён к этому процессу admin-api (тот же приём, что у
     * `lastReactionRun` выше). `.default(null)` по правилу окна выката.
     */
    refund: RiskCaseRefundSummaryDTO.nullable().default(null),
});
/**
 * Фильтры списка кейсов. Значения приезжают из query-string, поэтому числовые
 * поля — `z.coerce`. `cursor` — НЕПРОЗРАЧНЫЙ составной токен предыдущей
 * страницы: клиент возвращает его дословно и не разбирает (та же конвенция,
 * что у списка диалогов).
 */
export const RiskCaseListQuery = z.object({
    source: RiskSourceSchema.optional(),
    network: RiskNetworkSchema.optional(),
    kind: RiskAlertKindSchema.optional(),
    identificationStatus: RiskIdentificationStatusSchema.optional(),
    /**
     * Ось РЕАКЦИИ (`risk_case.reaction_status`), независимая от `identification
     * Status`/`actionRequired` рядом — «что сделала автоматика», а не «разобран
     * ли кейс человеком». `escalated` — операторская очередь «Требует рук» в
     * admin-web: серверный фильтр, а не клиентский над уже загруженной
     * страницей — тот способ терял эскалированные кейсы за пределами первой
     * страницы keyset-пагинации (это и есть очередь эскалаций, которую нельзя
     * молча урезать).
     */
    reactionStatus: RiskCaseReactionStatusSchema.optional(),
    // СТРОГИЙ enum, а НЕ `z.coerce.boolean()`. `Boolean('false') === true`, то
    // есть `?actionRequired=false` вернул бы ПРОТИВОПОЛОЖНЫЙ список — очередь
    // «требует решения» вместо «не требует», молча и без единого признака
    // ошибки. Та же форма, что у `hasScheduledEmail` и `awaitingOurReply` в
    // `conversations.ts`; опечатка обязана давать 400.
    actionRequired: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
    /**
     * Границы диапазона суммы — В ЦЕНТАХ, теми же единицами, что `amountCents`
     * строки списка, и без `nonnegative()`: по кейсам с реверсалом оператор
     * ищет именно «меньше нуля».
     */
    amountFrom: z.coerce.number().int().optional(),
    amountTo: z.coerce.number().int().optional(),
    /** Свободный поиск: последние 4 цифры карты, дескриптор, номер кейса. */
    q: z.string().trim().min(1).max(200).optional(),
    cursor: z.string().min(1).max(200).optional(),
    limit: z.coerce.number().int().positive().max(200).optional(),
});
export const RiskCaseListPageDTO = z.object({
    items: z.array(RiskCaseListItemDTO),
    nextCursor: z.string().min(1).nullable(),
    /**
     * Сколько кейсов подходит под ФИЛЬТРЫ — не под страницу и не «сколько
     * осталось после курсора».
     *
     * Числа страниц рядом с ним нет и не будет: keyset-пагинация не знает, что
     * впереди, — она умеет только «дай следующие N после этой строки». Это её
     * цена за то, что страница не разъезжается, когда пока читаешь, приходит
     * новый алерт. «Сколько всего» — вопрос отдельный, и он отвечаемый.
     *
     * `nullable().default(null)` по правилу окна выката: новый бандл читает
     * поле, которого старый admin-api ещё не отдаёт. Читатель проверяет
     * `!= null` и без счётчика просто не рисует подпись.
     */
    total: z.number().int().nonnegative().nullable().default(null),
});
/**
 * Смена НАШЕГО статуса разбора кейса.
 *
 * Значения и их смысл — в `RiskAlertStore.setCaseStatus`
 * (`packages/db/src/risk-alert-store.ts`), там же довод про свободные
 * переходы. Коротко: `open` — не разобран, `actioned` — решение отправлено в
 * источник (ставится автоматически при успешной отправке reason-кода, но
 * выбирается и руками — решение бывает отправлено мимо нас), `resolved` —
 * разобран и закрыт, `dismissed` — действий не требует.
 */
export const RiskCaseStatusRequest = z.object({
    status: RiskCaseStatusSchema,
});
/** Ручная привязка клиента к кейсу. */
export const RiskCaseIdentityRequest = z.object({
    witUserId: z.string().trim().min(1).max(200),
});
/**
 * Отправка решения по алерту в источник. `result` — строка reason-кода
 * источника (`AlertReasonCode` у Chargeblast). Намеренно `string`, а не
 * enum по `RISK_ACTION_REASON_CODES`: набор кодов принадлежит ИСТОЧНИКУ, а не
 * нашей модели, меняется на его стороне, и второй источник принесёт свой —
 * протухшая копия заблокировала бы оператору легальный код. Справочник
 * `RISK_ACTION_REASON_CODES` наполняет выпадающий список UI; валидность
 * значения проверяет адаптер источника.
 */
export const RiskAlertActionRequest = z.object({
    result: z.string().trim().min(1).max(200),
    actionAtExpiration: z.boolean().optional(),
});
export const RiskSyncRunDTO = z.object({
    id: z.guid(),
    accountId: z.guid(),
    source: RiskSourceSchema,
    trigger: z.enum(['poll', 'backfill']),
    status: z.enum(['success', 'partial', 'failed']),
    startedAt: z.string().datetime(),
    finishedAt: z.string().datetime().nullable().default(null),
    fetched: z.number().int().nonnegative(),
    upserted: z.number().int().nonnegative(),
    errored: z.number().int().nonnegative(),
    windowFrom: z.string().datetime().nullable().default(null),
    windowTo: z.string().datetime().nullable().default(null),
    error: z.string().nullable().default(null),
});
export const RiskSyncRunsQuery = z.object({
    limit: z.coerce.number().int().positive().max(200).optional(),
});
/**
 * Ответ rail-панели чата (`GET /api/conversations/:id/chargebacks`). Пустой
 * список — нормальное состояние: связь «кейс ↔ диалог» вычисляется на чтении
 * и у большинства диалогов кейсов нет.
 */
export const ConversationChargebacksResponse = z.object({
    cases: z.array(RiskCaseListItemDTO).default([]),
});
/**
 * Статус аккаунта источника. `disabled` ставит не только оператор: sweeper
 * синхронизации (`createRiskSyncSweeper`) сам гасит аккаунт, у которого секрет
 * не расшифровывается или пуст, — иначе каждый тик молча ходил бы в провайдер
 * с мусорным ключом. Поэтому «включить обратно» — законная операторская
 * операция, а не признак ошибки.
 */
export const RiskAccountStatusSchema = z.enum(['active', 'disabled']);
/**
 * Аккаунт источника риска.
 *
 * КЛЮЧА ЗДЕСЬ НЕТ — ни открытым текстом, ни маской. Это ОСОЗНАННОЕ отличие от
 * `ChannelAccountDTO`, который отдаёт `config` с секретами под маской `••••`, а
 * владельцу — расшифрованными (политика `channels.read_secrets`). У каналов
 * такое чтение нужно по делу: оператор сверяет client_id/redirect_uri OAuth-
 * приложения, и без просмотра значений эту сверку сделать нечем. Здесь секрет
 * ровно один и сверять в нём нечего — оператору нужен ответ на единственный
 * вопрос «ключ вообще задан?», и его несёт {@link RiskAccountDTO.hasApiKey}.
 * Поле `config` не входит в DTO вовсе, поэтому утечь ему неоткуда: маппер
 * роута не получает его даже на вход (стор отдаёт готовый `hasApiKey`), и
 * политики `chargebacks.read_secrets` не существует.
 *
 * `lastSyncRun` — последний прогон синхронизации ЭТОГО аккаунта: без него
 * «алерты перестали приходить» замечают через неделю (ровно так прошёл
 * gmail-простой 27 июля). Форма переиспользует {@link RiskSyncRunDTO}, а не
 * заводит усечённую копию: две формы одной строки разъезжаются на первом же
 * добавленном поле.
 *
 * `alertCount` нужен UI, чтобы объяснить недоступное удаление: аккаунт с
 * загруженными алертами удалить нельзя (см. `DELETE /api/chargebacks/accounts/:id`).
 */
export const RiskAccountDTO = z.object({
    id: z.guid(),
    source: RiskSourceSchema,
    name: z.string(),
    status: RiskAccountStatusSchema,
    /** Задан ли непустой API-ключ. Само значение наружу не отдаётся никогда. */
    hasApiKey: z.boolean(),
    alertCount: z.number().int().nonnegative().default(0),
    lastSyncRun: RiskSyncRunDTO.nullable().default(null),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const RiskAccountListResponse = z.object({
    accounts: z.array(RiskAccountDTO).default([]),
});
/**
 * Заведение аккаунта. Ключ ОБЯЗАТЕЛЕН: аккаунт без ключа sweeper на первом же
 * тике переведёт в `disabled` — заводить заведомо нерабочую строку и потом
 * объяснять оператору, почему она погасла сама, хуже, чем потребовать ключ
 * сразу.
 */
export const CreateRiskAccountRequest = z.object({
    source: RiskSourceSchema,
    name: z.string().trim().min(1).max(200),
    apiKey: z.string().trim().min(1).max(500),
    status: RiskAccountStatusSchema.optional(),
});
/**
 * Изменение аккаунта. `apiKey` НЕОБЯЗАТЕЛЕН, и пустая строка равнозначна его
 * отсутствию: оператор, меняющий имя или статус, не обязан держать ключ под
 * рукой. Тот же инвариант, что у каналов (там роль «не трогать» играет
 * сентинел `••••`) и у `pnpm risk:seed` (там — мердж jsonb): затереть рабочий
 * ключ пустотой значит остановить загрузку алертов, причём незаметно.
 *
 * `source` не редактируется: сменить источник у аккаунта — это другой аккаунт,
 * а уже загруженные алерты ссылаются на этот. Ровно так же неизменяем
 * `channel_account.kind`.
 */
export const UpdateRiskAccountRequest = z.object({
    name: z.string().trim().min(1).max(200).optional(),
    status: RiskAccountStatusSchema.optional(),
    apiKey: z.string().trim().max(500).optional(),
});
/**
 * Результат проверки соединения. Всегда приезжает с HTTP 200 (кроме 403/404):
 * «провайдер ответил 401» — это ИСХОД ПРОВЕРКИ, а не сбой запроса к нам, и
 * рисовать его красным баннером «ошибка админки» неверно. Та же конвенция, что
 * у `POST /api/payment/test`.
 *
 * `fetched` — сколько записей вернул провайдер на пробной странице (`per=1`),
 * то есть 0 или 1. Ноль — не ошибка: у аккаунта может не быть свежих алертов.
 */
export const RiskAccountTestResultDTO = z.object({
    ok: z.boolean(),
    fetched: z.number().int().nonnegative().nullable().default(null),
    error: z.string().nullable().default(null),
});
// ─────────────────────── Журнал отправленных решений ───────────────────────
/**
 * Строка ЖУРНАЛА отправленных решений (`GET /api/chargebacks/alert-actions`).
 *
 * Это та же `risk_alert_action`, что приезжает вложенной в алерт
 * (`RiskAlertDTO.sentAction`), плюс координаты, без которых строка журнала
 * никуда не ведёт. Журнал читают ПОПЕРЁК алертов: по строке «Resolved, исход
 * не подтверждён» оператор обязан попасть в кейс и понять, о чём она, — иначе
 * такую строку не находит никто, а сверять её с источником всё равно придётся
 * руками. Отсюда `alertId`, `network` и ссылка на кейс.
 *
 * `caseId` и `caseNumber` nullable ОБА, но по разным причинам, и схлопывать их
 * в одно поле нельзя. `caseId` пуст, пока алерт не приклеился к кейсу
 * (`risk_alert.case_id` проставляет корреляция, и строка действия в принципе
 * может быть записана раньше). `caseNumber` пуст ещё и по правилу окна
 * выката — тем же, что у `ConversationDTO.number`: новый бандл читает поле,
 * которого старый admin-api ещё не отдаёт.
 */
export const RiskAlertActionLogItemDTO = RiskAlertActionDTO.extend({
    alertId: z.guid(),
    /** Сеть алерта — чтобы строка журнала читалась без перехода в кейс. */
    network: RiskNetworkSchema.nullable().default(null),
    /** Кейс алерта; `null` — алерт ещё не приклеился ни к одному кейсу. */
    caseId: z.guid().nullable().default(null),
    /** Короткий номер кейса для ссылки. */
    caseNumber: z.number().int().nullable().default(null),
});
/**
 * Запрос журнала решений.
 *
 * `status` — не косметический фильтр, а рабочая очередь. `in_flight` означает
 * «отправили, исход не подтверждён»: вызов источника оборвался, применился
 * апдейт или нет — не знает никто. Такие строки живут, пока человек не
 * сверится с Chargeblast вручную, и без возможности выбрать ровно их оператор
 * ищет их глазами по всему журналу.
 *
 * `cursor` — НЕПРОЗРАЧНЫЙ составной токен `${createdAt}|${id}` последней
 * строки предыдущей страницы. Ответ — ПЛОСКИЙ массив (форма зафиксирована
 * клиентом раздела), поэтому следующий курсор клиент собирает из последней
 * строки сам: `createdAt` уезжает на провод ровно тем же `toISOString()`, из
 * которого курсор и собирается на сервере.
 *
 * Пагинация композитная, а не по одной метке времени, и это не
 * перестраховка: `created_at` строк действия слипается по построению — одна
 * отправка пишет строку `in_flight` и почти сразу её закрытие, а бэкфилл
 * журнала слепит и больше. На слипшемся блоке голый `WHERE ts < :cursor`
 * теряет остаток безвозвратно (ровно эта авария уже была на инбоксе 13.08).
 */
export const RiskAlertActionsQuery = z.object({
    status: z.enum(['in_flight', 'sent', 'failed']).optional(),
    cursor: z.string().min(1).max(200).optional(),
    limit: z.coerce.number().int().positive().max(200).optional(),
});
// ───────────────────────── Правила реакций (rules) ─────────────────────────
/**
 * Форма правила на проводе — зеркало `RiskReactionRuleRow` из
 * `@wit-support/db`. `conditions`/`steps` типизированы доменными схемами
 * `@wit-support/risk-alerts` (`RiskReactionExprSchema` /
 * `RiskReactionStepsConfigSchema`), а НЕ схемой запроса ниже
 * (`RiskReactionConditionsRequestSchema`): та несёт дополнительную проверку
 * пары оператор↔значение, нужную только на ЗАПИСИ (форма редактора). На
 * ЧТЕНИИ она избыточна — правило уже прошло эту проверку при сохранении, и
 * повторный прогон здесь означал бы, что чтение может уронить карточку
 * правила на данных, которые сам же роут только что записал.
 */
export const RiskReactionRuleDTO = z.object({
    id: z.guid(),
    /** Порядок применения — первое совпавшее ВКЛЮЧЁННОЕ правило выигрывает. */
    position: z.number().int(),
    name: z.string(),
    enabled: z.boolean(),
    conditions: RiskReactionExprDomainSchema,
    steps: RiskReactionStepsConfigSchema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const RiskReactionRuleListResponse = z.object({
    rules: z.array(RiskReactionRuleDTO).default([]),
});
/**
 * Операторы, ИГНОРИРУЮЩИЕ `value` (проверка присутствия / строгого булева).
 * Копия `VALUELESS_OPERATORS` из `packages/pipeline/src/definition.ts` — тот
 * же список, тот же смысл, но НЕ импортируется оттуда: список — три строковых
 * литерала, дублировать их дешевле, чем тянуть на публичный экспорт пакета
 * pipeline константу, которая там сегодня приватна модулю.
 */
const VALUELESS_REACTION_OPERATORS = new Set(['exists', 'not_exists', 'is_true', 'is_false']);
/**
 * Рекурсивная проверка пары оператор↔значение на каждом `field`-листе дерева
 * условий правила — ровно то, чего сознательно НЕ делает доменная
 * `RiskReactionExprSchema` (`@wit-support/risk-alerts`, T4 в брифе задачи 12):
 * она проверяет, что `(source, path)` известны каталогу и что оператор
 * применим к типу поля, но не то, что у `exists`/`not_exists` нет `value`, а у
 * `in` оно — непустой массив. Копия идеи `FieldMatchConditionNodeSchema`
 * (`packages/pipeline/src/definition.ts:110-153`), адаптированная под форму
 * листа `RiskReactionExpr` (`source`+`path`, а не `fieldPath`).
 *
 * Дерево уже прошло структурный гвард доменной схемы (глубина ≤20, узлов
 * ≤1000) — обычная рекурсия здесь безопасна для стека.
 */
const validateReactionExprLeaves = (expr, ctx, path) => {
    switch (expr.type) {
        case 'always':
            return;
        case 'field': {
            const { operator, value } = expr;
            if (VALUELESS_REACTION_OPERATORS.has(operator)) {
                if (value !== undefined) {
                    ctx.addIssue({
                        code: 'custom',
                        path: [...path, 'value'],
                        message: `Оператор «${operator}» не принимает значение`,
                    });
                }
                return;
            }
            if (operator === 'in') {
                if (!Array.isArray(value) || value.length === 0) {
                    ctx.addIssue({
                        code: 'custom',
                        path: [...path, 'value'],
                        message: 'Оператор «in» требует непустой список значений',
                    });
                }
                return;
            }
            // Остальные операторы (equals/not_equals/gt/gte/lt/lte/contains) требуют
            // ОДНО скалярное значение.
            if (value === undefined || Array.isArray(value)) {
                ctx.addIssue({
                    code: 'custom',
                    path: [...path, 'value'],
                    message: `Оператор «${operator}» требует одно значение`,
                });
            }
            return;
        }
        case 'all':
        case 'any':
            expr.of.forEach((child, i) => validateReactionExprLeaves(child, ctx, [...path, 'of', i]));
            return;
        case 'not':
            validateReactionExprLeaves(expr.of, ctx, [...path, 'of']);
            return;
    }
};
/**
 * Схема условий для ЗАПРОСА (create/update правила) — доменное дерево
 * `RiskReactionExprSchema` плюс проверка оператор↔значение выше. Отдельно от
 * `RiskReactionExprDomainSchema`, которым читается уже сохранённое правило
 * (см. довод у `RiskReactionRuleDTO.conditions`).
 */
export const RiskReactionConditionsRequestSchema = RiskReactionExprDomainSchema.superRefine((expr, ctx) => validateReactionExprLeaves(expr, ctx, []));
/**
 * §7 спеки: `respondAlert: true` без `refund: true` — правило отвечает
 * провайдеру решением («разобрано»), не возвращая клиенту деньги. Это
 * ЕДИНСТВЕННАЯ комбинация степов, которая гораздо чаще похожа на забытый
 * чекбокс `refund`, чем на осознанное решение (класс «dismiss»: сеть
 * уведомили, возврата по этой причине нет), поэтому требует явного
 * подтверждения `confirmNoRefundResponse: true` в теле запроса —
 * ОДНОРАЗОВОГО флага запроса, который НЕ сохраняется в самом правиле (его нет
 * ни в `RiskReactionRuleDTO`, ни в `RiskReactionStepsConfigSchema`).
 */
const validateRespondAlertNeedsRefundOrConfirm = (steps, confirmNoRefundResponse, ctx) => {
    if (steps.respondAlert && !steps.refund && confirmNoRefundResponse !== true) {
        ctx.addIssue({
            code: 'custom',
            path: ['steps', 'respondAlert'],
            message: 'Правило отвечает провайдеру решением БЕЗ возврата денег клиенту. ' +
                'Если это осознанно, подтвердите confirmNoRefundResponse.',
        });
    }
};
export const CreateRiskReactionRuleRequest = z
    .object({
    name: z.string().trim().min(1).max(200),
    conditions: RiskReactionConditionsRequestSchema,
    steps: RiskReactionStepsConfigSchema,
    /** Одноразовое подтверждение — см. `validateRespondAlertNeedsRefundOrConfirm`. */
    confirmNoRefundResponse: z.boolean().optional(),
})
    .superRefine((body, ctx) => validateRespondAlertNeedsRefundOrConfirm(body.steps, body.confirmNoRefundResponse, ctx));
/**
 * Частичное изменение правила. `steps` — ВЕСЬ объект, а не под-поле: стор
 * (`updateRule`) переписывает `steps` целиком, партиального патча jsonb-поля
 * не существует, поэтому и проверка `respondAlert`↔`refund` применяется
 * только когда `steps` присутствует в теле — если его нет, комбинация степов
 * не меняется этим вызовом вовсе, и подтверждение спрашивать не о чем.
 */
export const UpdateRiskReactionRuleRequest = z
    .object({
    name: z.string().trim().min(1).max(200).optional(),
    enabled: z.boolean().optional(),
    conditions: RiskReactionConditionsRequestSchema.optional(),
    steps: RiskReactionStepsConfigSchema.optional(),
    confirmNoRefundResponse: z.boolean().optional(),
})
    .superRefine((body, ctx) => {
    if (body.steps !== undefined) {
        validateRespondAlertNeedsRefundOrConfirm(body.steps, body.confirmNoRefundResponse, ctx);
    }
});
/**
 * Полная переупаковка порядка. `orderedIds` ОБЯЗАН содержать ВСЕ
 * существующие id правил, ровно по одному разу — маршрут (не эта схема, ей
 * неоткуда знать текущий список) сверяет это со `listRules()` и отвечает 422
 * на неполном/лишнем списке. Требование полноты — не проверка данных, а
 * защита смысла: `reorderRules` в сторе пишет позиции ОДНОЙ транзакцией по
 * переданному списку и не умеет «оставить прочие как есть» — частичный список
 * молча сдвинул бы порядок непереданных правил на произвольные позиции.
 */
export const ReorderRiskReactionRulesRequest = z.object({
    orderedIds: z.array(z.guid()).min(1),
});
// ─────────────────── Прогоны реакций (runs) — запрос страницы ───────────────
//
// `RiskReactionRunDTO` сам объявлен ВЫШЕ, рядом с `RiskCaseReactionStatusSchema`
// — `RiskCaseDetailDTO.lastReactionRun` на него ссылается напрямую, без
// `z.lazy`, и это требует прямого (не вперёд) порядка объявления.
/**
 * Запрос страницы прогонов. `caseId` сужает журнал до одного кейса (карточка
 * кейса могла бы использовать этот же маршрут для полной истории, а не
 * только последнего прогона) — без него это ЖУРНАЛ ПОПЕРЁК ВСЕХ кейсов.
 * `cursor` — тот же непрозрачный композитный токен `${ISO}|${id}`, что и у
 * `/alert-actions` (`RiskAlertActionsQuery`).
 */
export const RiskReactionRunsQuery = z.object({
    caseId: z.guid().optional(),
    cursor: z.string().min(1).max(200).optional(),
    limit: z.coerce.number().int().positive().max(200).optional(),
});
export const RiskReactionRunsPageDTO = z.object({
    items: z.array(RiskReactionRunDTO).default([]),
    nextCursor: z.string().min(1).nullable(),
});
// `POST /api/chargebacks/cases/:id/react` не несёт тела: `caseId` едет в URL,
// `requestedByAdminId` роут берёт из сессии, а не из запроса — доверять
// клиенту чужой id значило бы разрешить подписать чужую реакцию своим
// запросом. Отдельной Zod-схемы запроса поэтому нет.
// ──────────────────── Тумблеры автоматики реакций (task 14) ────────────────
//
// Четыре флага `app_settings` одним объектом. Ключи и форма ХРАНИМОГО
// значения живут в `@wit-support/risk-alerts` (`settings.ts`) — их читает не
// только админка, но и воркер со свипером; здесь только ПРОВОДНОЙ контракт
// `/api/chargebacks/automation`.
/**
 * ЧТЕНИЕ (`GET`). Все четыре поля обязательны и всегда несут разрешённое
 * значение: отсутствующий ключ в `app_settings` — это `false`, а не «поля
 * нет». Отдельная схема от запроса ниже по той же причине, что у
 * `LlmSettings`: ответ всегда полон, даже если тело запроса частичное.
 */
export const RiskAutomationSettingsDTO = z.object({
    /** `risk.reactions_enabled` — боевая основная ветка (возврат/отмена/ответ). */
    reactionsEnabled: z.boolean(),
    /** `risk.duplicate_auto_response` — ветка дубликатов, платёжный сервис не нужен. */
    duplicateAutoResponse: z.boolean(),
    /** `risk.reactions_shadow` — полный прогон правил с журналом, без внешних действий. */
    reactionsShadow: z.boolean(),
    /** `risk.reaction_sweep_enabled` — свипер дедлайна (эскалация + реклейм). */
    sweepEnabled: z.boolean(),
});
/**
 * ЗАПИСЬ (`PUT`). ВСЕ четыре поля обязательны — в отличие от частичного
 * `UpdateLlmSettingsRequest`. Причина в цене ошибки: экран показывает четыре
 * тумблера ОДНОВРЕМЕННО, и оператор принимает решение, глядя на их
 * КОМБИНАЦИЮ («shadow включён, боевые выключены»). PUT целиком означает, что
 * записанное состояние — ровно то, которое оператор видел; частичный патч
 * позволил бы вкладке, открытой час назад, включить боевые реакции, не зная,
 * что shadow за это время выключили. `.strict()` — опечатка в поле должна
 * падать, а не молча ничего не менять.
 */
export const UpdateRiskAutomationSettingsRequest = z
    .object({
    reactionsEnabled: z.boolean(),
    duplicateAutoResponse: z.boolean(),
    reactionsShadow: z.boolean(),
    sweepEnabled: z.boolean(),
})
    .strict();
//# sourceMappingURL=chargebacks.js.map